import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Printer, Mail, Clock, ArrowRight, Loader2 } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-transparent border-b-2 border-white/20 rounded-none py-3.5 px-0 text-white placeholder:text-white/35 font-sans focus:outline-none focus:border-spartanorange transition-colors duration-200";

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex gap-4 py-5 border-b border-white/10">
    <Icon size={18} className="text-spartanorange shrink-0 mt-1" />
    <div>
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">
        {label}
      </p>
      <p className="text-white/85 text-sm mt-1 leading-relaxed">{children}</p>
    </div>
  </div>
);

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    machinery_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent. Our team will contact you shortly.");
      setForm({ name: "", email: "", company: "", phone: "", machinery_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-obsidian py-24 md:py-32 border-t border-white/10"
      data-testid="contact-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: heading + info */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-spartanorange" />
                <span className="font-mono text-xs tracking-[0.25em] uppercase text-white/70">
                  Contact — No Obligation
                </span>
              </div>
              <h2 className="font-display uppercase tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.88]">
                Let&apos;s build
                <br />
                <span className="text-spartanorange">your line</span>
              </h2>
            </motion.div>

            <div className="mt-10">
              <InfoRow icon={MapPin} label="Address">{COMPANY.address}</InfoRow>
              <InfoRow icon={Phone} label="Phone">
                <a href={`tel:${COMPANY.phone}`} className="hover:text-spartanorange transition-colors">{COMPANY.phone}</a>
              </InfoRow>
              <InfoRow icon={Printer} label="Fax">{COMPANY.fax}</InfoRow>
              <InfoRow icon={Mail} label="Email">
                <a href={`mailto:${COMPANY.email}`} className="hover:text-spartanorange transition-colors">{COMPANY.email}</a>
              </InfoRow>
              <InfoRow icon={Clock} label="Business Hours">{COMPANY.hours}</InfoRow>
            </div>
          </div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8" data-testid="contact-form">
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">Full Name *</label>
                <input data-testid="contact-name" value={form.name} onChange={update("name")} className={FIELD} placeholder="Jane Doe" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">Email *</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} className={FIELD} placeholder="jane@company.com" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">Company</label>
                <input data-testid="contact-company" value={form.company} onChange={update("company")} className={FIELD} placeholder="Company Sdn Bhd" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">Phone</label>
                <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className={FIELD} placeholder="+60 12 345 6789" />
              </div>
              <div className="md:col-span-2">
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">Machinery Type</label>
                <select data-testid="contact-machinery" value={form.machinery_type} onChange={update("machinery_type")} className={`${FIELD} cursor-pointer`}>
                  <option value="" className="bg-obsidian">Select a solution…</option>
                  {SERVICES.map((s) => (
                    <option key={s.title} value={s.title} className="bg-obsidian">{s.title}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">Your Requirements *</label>
                <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={4} className={`${FIELD} resize-none`} placeholder="Tell us about your production line and packaging needs…" />
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-3 bg-spartanorange text-obsidian font-mono text-sm tracking-[0.15em] uppercase px-9 py-5 hover:bg-white transition-colors duration-200 disabled:opacity-60"
                >
                  {loading ? (
                    <>Sending<Loader2 size={18} className="animate-spin" /></>
                  ) : (
                    <>Send Enquiry <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" /></>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
